import { RowsIcon, SquaresFourIcon } from '@phosphor-icons/react'
import { ReactNode, useCallback, useMemo, useState } from 'react'
import { useLocation, useSearchParams } from 'react-router-dom'
import { ContactDrawer } from 'app/components/contacts/ContactDrawer'
import { ListBoardHeader } from 'app/components/lists/board/ListBoardHeader'
import { BulkSelectToggle } from 'app/components/lists/board/BulkSelectToggle'
import { useBoardSelection } from 'app/components/lists/board/useBoardSelection'
import { BulkActionBar } from 'app/components/table/bulkActions/BulkActionBar'
import type { BulkSelection } from 'app/components/table/bulkActions/types'
import { PageScroll } from 'app/components/layout/PageScroll'
import { Search } from 'app/components/Search'
import { TableFiltersView } from 'app/components/ui/table-filters/table-filters'
import type { FixedRules } from 'app/types/filters'
import { useDrawerContact } from 'app/hooks/useDrawerContact'
import type { FilterableColumn } from 'app/filter.utils'
import { useHandleSearchParams, useUser } from 'app/utils'
import { CaseDetail } from './CaseDetail'
import { preferredView, rememberView } from './boardView'
import { SegmentedControl } from './SegmentedControl'
import { queueNavSearch } from './locationParam'
import { DEFAULT_TREATMENT_BAND, TREATMENT_BANDS, TreatmentBandId, bandFromParamOrDefault } from './treatmentBands'
import { ALL_BANDS } from 'lib/systemLists'
import { SortPicker } from './SortPicker'
import { PROCEDURE_FILTER_COLUMNS } from './procedureFilter'
import { NEXT_VISIT_RULE_COLUMN, SEEN_BEFORE_RULE_COLUMN, bandRuleColumn } from 'lib/filterRuleColumns'
// `TreatmentView` is not `QueueView`'s `focus`: there is no one-at-a-time flow
// for all of a band at once.
import { BandSort, TreatmentView, bandSortFor } from './treatmentSort'
import { TreatmentColumns } from './TreatmentColumns'
import { TreatmentTable } from './TreatmentTable'
import { useTreatmentBulkActions } from './useTreatmentBulkActions'
import { BOARD_SORTS, BoardSort, TREATMENT_PIPELINE_ID, TreatmentRow, useTreatmentBoard } from './useTreatmentBoard'
import { useTreatmentCaseBoard } from './useTreatmentCaseBoard'

const VIEW_OPTIONS: { id: TreatmentView; label: string; icon: React.ReactElement; blurb: string }[] = [
  {
    id: `board`,
    label: `Board`,
    icon: <SquaresFourIcon size={14} weight="bold" />,
    blurb: `Hold several conversations in flight at once`,
  },
  {
    id: `table`,
    label: `Table`,
    icon: <RowsIcon size={14} weight="bold" />,
    blurb: `Scan the whole band and sort it`,
  },
]

// "Presented" is the practice's word for it, though the date is when the plan was made (see `treatmentBands.ts`).
const PRESENTED_RULE_COLUMN = bandRuleColumn({
  name: `Presented`,
  bands: TREATMENT_BANDS.map((band) => ({ id: band.id, label: band.shortName })),
  defaultBand: DEFAULT_TREATMENT_BAND,
})

/**
 * The board view's bulk bar: the same actions as the table's, stage move first. Mounted only
 * while the board is selecting, so the pipelines and workflows its menu offers are read only
 * once somebody asks for it, as the table does.
 */
function TreatmentBoardBulkBar({
  board,
  selection,
  onExit,
}: {
  board: ReturnType<typeof useTreatmentBoard>
  selection: BulkSelection<TreatmentRow>
  onExit: () => void
}) {
  const config = useTreatmentBulkActions(board.setStage, board.destinations)
  return <BulkActionBar selection={selection} config={config} onExit={onExit} />
}

/**
 * Unscheduled treatment, as a board of conversations or as a table of cases.
 *
 * This component owns everything the two views share: which band is on
 * screen, the search and location drill-down, the sort, the case dialog and
 * the patient drawer. The views themselves own only their own furniture, so
 * flipping between them changes how the band is drawn and nothing about which
 * band it is.
 */
export function TreatmentBoardComponent({
  view: viewProp,
  headerControls,
  titleActions,
  subheader,
  bandColumn = PRESENTED_RULE_COLUMN,
  title = `Unscheduled treatment`,
  totalItems,
  band: listBand,
  hideExits,
  fixedRules,
}: {
  /** Wins over the URL/preference resolution below when given; a list page already knows the view. */
  view?: TreatmentView
  /** The header title. A list page passes its own name so the page matches the sidebar row. */
  title?: string
  /**
   * A list page's view switch, drawn at the right end of the controls row. When
   * given, the page is the board's home: no back link, and the board's own view toggle stays
   * off so the one switch sits where every view of the list puts it.
   */
  headerControls?: ReactNode
  titleActions?: ReactNode
  subheader?: ReactNode
  /** A list page's band rule, which replaces the board's own in the Filters builder. */
  bandColumn?: FilterableColumn
  fixedRules?: FixedRules
  /** The list's count for the title chip; the sidebar row's number. */
  totalItems?: number
  /**
   * The band the list page resolved, `all` for every band. The page decides per view (a
   * bare table URL opens on every band, the board on its default), so when it hands one
   * over the board does not re-read the URL and fall back to its own default.
   */
  band?: TreatmentBandId | typeof ALL_BANDS
  /** Set by the Unscheduled Treatment list page, whose exit statuses take a contact off the board. */
  hideExits?: boolean
}) {
  const routerLocation = useLocation()
  const user = useUser()
  const [sort, setSort] = useState<BoardSort>(`presented`)
  // Only the table's column headers ever ask for the other end of a sort; the
  // board's own picker is biggest-and-freshest-first and nothing else.
  const [direction, setDirection] = useState<`ASC` | `DESC`>(`DESC`)

  const [searchParams] = useSearchParams()
  const handleSearchParams = useHandleSearchParams()
  const band =
    listBand === undefined
      ? bandFromParamOrDefault(searchParams.get(`band`))
      : listBand === ALL_BANDS
        ? undefined
        : listBand

  // The URL wins so a view survives a reload and travels in a shared link;
  // the remembered preference only decides what an unqualified link opens on.
  // Stored under the pipeline id rather than a queue's: this board is all four
  // treatment queues at once, so no single queue's key would be honest.
  const viewParam = searchParams.get(`view`)
  const view: TreatmentView =
    viewProp ??
    (viewParam === `table` || viewParam === `board`
      ? viewParam
      : preferredView(user?.id, TREATMENT_PIPELINE_ID, `board`) === `table`
        ? `table`
        : `board`)
  const setView = (next: TreatmentView) => {
    rememberView(user?.id, TREATMENT_PIPELINE_ID, next)
    handleSearchParams({ view: next })
    // The table keeps a selection of its own; one picked on the board does not follow it there.
    bulk.exit()
  }

  // The board never runs the table's direction: see `bandSortFor`.
  const querySort = bandSortFor(view, { sort, direction })

  // The open patient lives in `?drawer=`, so a link or a reload opens the same record.
  // handleSearchParams reads the live URL when it runs, so the first render's copy serves.
  const drawer = useDrawerContact()
  const openRecord = useCallback((contactId: string) => {
    handleSearchParams({ drawer: contactId })
    return true
  }, [])

  const cases = useTreatmentCaseBoard({
    sort: querySort.sort,
    direction: querySort.direction,
    band,
    hideExits,
    openRecord,
  })
  const { board } = cases

  // Only the cases in the columns: the table's rows also carry the ones at an exit, which
  // the board does not draw and so cannot have picked.
  const visibleIds = useMemo(
    () => new Set(board.columns.flatMap((column) => column.cards.map((card) => card.id))),
    [board.columns],
  )
  const bulk = useBoardSelection(visibleIds)
  const bulkSelection = useMemo<BulkSelection<TreatmentRow>>(
    () => ({ rows: board.rows.filter((row) => bulk.selected.has(row.id)), clear: bulk.clear }),
    [board.rows, bulk.selected, bulk.clear],
  )

  // Only a failure gets a banner now. A read in flight and an empty band both
  // used to get one too, and both were saying a second time what the thing
  // underneath already said better: the columns read "Nothing here" a stage at
  // a time, and the table draws its own empty state and its own spinner. A
  // strip of grey above them was one more thing on screen and no more
  // information. A failure is different: nothing below it can report that.

  const onSort = useCallback((next: BandSort) => {
    setSort(next.sort)
    setDirection(next.direction)
  }, [])
  const bandSort = useMemo<BandSort>(() => ({ sort, direction }), [sort, direction])

  const filterColumns = useMemo(
    () => [bandColumn, NEXT_VISIT_RULE_COLUMN, SEEN_BEFORE_RULE_COLUMN, ...PROCEDURE_FILTER_COLUMNS],
    [bandColumn],
  )

  const filterControls = (
    <>
      <Search />
      {/* The contacts table's Filters builder, with rows about the treatment
          rather than the person; the location is one of its columns. */}
      <TableFiltersView columns={filterColumns} fixedRules={fixedRules} />
    </>
  )

  // The board's own sort stays biggest-first. The table's headers reach the same two fields
  // and can turn them around, so the table draws no picker.
  const sortPicker = (
    <SortPicker
      options={BOARD_SORTS}
      value={sort}
      onSelect={(next) => {
        setSort(next)
        setDirection(`DESC`)
      }}
    />
  )

  // No focus option here, unlike a single band queue's toggle: this is every
  // band at once and there is no equivalent of working all of it one card at a
  // time.
  const viewToggle = headerControls ? undefined : (
    <SegmentedControl
      className="shrink-0"
      options={VIEW_OPTIONS.map((option) => ({
        id: option.id,
        label: option.label,
        icon: option.icon,
        title: option.blurb,
      }))}
      value={view}
      onSelect={setView}
    />
  )

  return (
    // `table` hands the scroll to the child, which is what a DataTable wants
    // and what every other table page in the app passes; the board keeps the
    // page-level scroll its columns are built against.
    // `relative` docks the board's bulk bar to the page rather than to the scrolling content.
    <PageScroll
      variant={view === `table` ? `table` : `page`}
      className="relative"
      contentClassName="flex h-full flex-col pb-6 pt-8"
    >
      {/* A running total of what had been booked used to sit in this header. It read as a
          scoreboard for the session rather than as anything to act on: those cases have
          left the board, and the exits under the lanes already count them and open the
          full list on a press. */}
      <ListBoardHeader
        backTo={headerControls ? undefined : `/action-center${queueNavSearch(routerLocation.search)}`}
        title={title}
        subheader={subheader}
        totalItems={totalItems}
        actions={titleActions}
        // The table view carries these same controls in its own toolbar, where the
        // row-lines and column controls already live, so the header draws them only for
        // the board. A list page's switch ends the row in both views.
        controls={view === `board` ? filterControls : undefined}
        headerControls={view === `board` ? headerControls : undefined}
        viewToggle={
          view === `board` ? (
            <div className="flex items-center gap-2">
              <BulkSelectToggle selecting={bulk.selecting} onToggle={bulk.toggleSelecting} />
              {sortPicker}
              {viewToggle}
            </div>
          ) : (
            viewToggle
          )
        }
        banner={
          board.error && (
            <div className="mt-4">
              <p className="min-w-0 rounded-lg bg-red-50 px-3 py-2 text-body-xs leading-relaxed text-red-900">
                {board.error}
              </p>
            </div>
          )
        }
      />

      {view === `board` ? (
        <TreatmentColumns board={board} sort={sort} onOpenCase={cases.openCase} selection={bulk.selection} />
      ) : (
        // The full page gutter, where every other table in the app sits, and
        // wider than the board's columns beside it. Narrowing this to the
        // columns' own width lines the two views up but costs the table two
        // columns to a horizontal scrollbar, and a table exists to be read
        // across.
        <div className="flex min-h-0 flex-1 flex-col">
          <TreatmentTable
            rows={board.rows}
            ready={cases.ready}
            loading={board.loading}
            hasMore={board.hasMore}
            loadMore={board.loadMore}
            sort={bandSort}
            controls={filterControls}
            // The board's own switch, or a list page's: either way the
            // row's right end, where the board view draws the same controls.
            rightControls={viewToggle ?? <>{headerControls}</>}
            onSort={onSort}
            onOpenCase={cases.openRowCase}
            onOpenRecord={cases.openRowRecord}
            setStage={board.setStage}
            stages={board.destinations}
            lockedStage={board.bookedStage}
          />
        </div>
      )}

      {view === `board` && bulk.selecting && (
        <TreatmentBoardBulkBar board={board} selection={bulkSelection} onExit={bulk.exit} />
      )}

      <CaseDetail {...cases.caseDetail} />

      <ContactDrawer {...drawer} />
    </PageScroll>
  )
}
